// ConnectSourceModal.tsx — the 4-step connect flow modal for the Add New
// Product (V6) screen. Transliterated from
// docs/design/design_handoff_add_new_product/newproduct-connect.jsx
// (NPCStepDot / NPCRow / NPConnectModal). Pixel values are design-final.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { NPGlyph, NPIcon } from "../atoms";
import type { SourceConnection, SourceSelectionItem } from "../state";
import { SOURCE_CONFIGS, type ConnectSourceId } from "./sourceConfigs";
import { getDriver, type ExistingAccount } from "./drivers";
import { TargetPicker } from "./TargetPicker";
import {
    cancelRepositoryImport,
    getRepositoryImports,
    repositoryImportIsActive,
    retryRepositoryImport,
    scheduleRepositoryImports,
    type RepositoryImportJob
} from "../repositoryImportsApi";

type Phase = "account" | "verify" | "project" | "ready" | "importing" | "done";
const ORDER: Phase[] = ["account", "verify", "project", "ready", "importing", "done"];
const idx = (p: Phase) => ORDER.indexOf(p);

type DotState = "done" | "active" | "upcoming";

function NPCStepDot({ state, n }: { state: DotState; n: number }) {
    if (state === "done") {
        return (
            <span style={{
                width: 22, height: 22, borderRadius: 11, background: "#059669",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto"
            }}>
                <NPIcon name="check" size={11} sw={3} color="#fff" />
            </span>
        );
    }
    if (state === "active") {
        return (
            <span style={{
                width: 22, height: 22, borderRadius: 11, background: "#103699", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
                fontSize: 11, fontWeight: 700, boxShadow: "0 3px 9px rgba(16, 54, 153,0.35)"
            }}>{n}</span>
        );
    }
    return (
        <span style={{
            width: 22, height: 22, borderRadius: 11, background: "var(--bg-paper)",
            border: "1.5px solid var(--divider)", color: "var(--fg4)",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
            fontSize: 11, fontWeight: 700
        }}>{n}</span>
    );
}

function NPCRow({ n, state, title, meta, action, children, isLast }: {
    n: number; state: DotState; title: string; meta?: React.ReactNode; action?: React.ReactNode;
    children?: React.ReactNode; isLast?: boolean;
}) {
    return (
        <div style={{ display: "flex", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <NPCStepDot state={state} n={n} />
                {!isLast && (
                    <span style={{
                        flex: 1, width: 2, minHeight: 14,
                        background: state === "done" ? "#a7f3d0" : "var(--divider)",
                        borderRadius: 2, margin: "3px 0"
                    }} />
                )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 14 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minHeight: 22 }}>
                    <span style={{
                        fontSize: 13.5, fontWeight: 650,
                        color: state === "upcoming" ? "var(--fg4)" : state === "active" ? "#103699" : "var(--fg1)"
                    }}>{title}</span>
                    {meta && <span style={{ fontSize: 12, color: "var(--fg3)" }}>{meta}</span>}
                    <span style={{ flex: 1 }} />
                    {action}
                </div>
                {children}
            </div>
        </div>
    );
}

function NPSpinner({ size = 18 }: { size?: number }) {
    return (
        <span
            style={{
                width: size, height: size, borderRadius: "50%",
                border: "2px solid var(--divider)", borderTopColor: "#103699",
                display: "inline-block", animation: "npc-spin 0.8s linear infinite", flex: "0 0 auto"
            }}
        >
            <style>{"@keyframes npc-spin { to { transform: rotate(360deg); } }"}</style>
        </span>
    );
}

function ErrorText({ message }: { message: string }) {
    return (
        <div style={{ fontSize: 12.5, color: "var(--error, #dc2626)", marginTop: 8 }}>{message}</div>
    );
}

function NPCAction({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <span
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClick(); }}
            style={{ fontSize: 12, fontWeight: 600, color: "#103699", cursor: "pointer" }}
        >
            {label}
        </span>
    );
}

export function ConnectSourceModal(props: {
    source: ConnectSourceId;
    token: string;
    userId: number;
    existing: SourceConnection;
    onComplete: (connection: SourceConnection) => void;
    onDisconnect: () => void;
    onClose: () => void;
    onAborted: (staged: { processJobId?: string; repoIds?: number[] }) => void;
    draftId?: number | null;
    /** Saves selected repository ids in the draft before any import job is scheduled. */
    onRepositorySelectionStaged?: (connection: SourceConnection) => Promise<{ draftId: number; draftVersion: number }>;
    /** Publishes authoritative import progress for FDE status answers. */
    onRepositoryImportsChanged?: (jobs: RepositoryImportJob[]) => void;
    /** Publishes generic integration progress for FDE status answers. */
    onIntegrationImportChanged?: (progress: {
        source: ConnectSourceId;
        sourceName: string;
        itemType: string;
        status: "importing" | "completed";
        current: number;
        total: number;
        selections: string[];
    } | null) => void;
}) {
    const { source, token, userId, existing, onComplete, onDisconnect, onClose, onAborted } = props;
    const cfg = SOURCE_CONFIGS[source];
    const driver = getDriver(source);
    const usesDurableRepositoryImport = Boolean(
        driver.durableRepositoryImport && props.onRepositorySelectionStaged
    );

    const [phase, setPhase] = useState<Phase>(existing.connected ? "done" : "account");
    const [account, setAccount] = useState<ExistingAccount | null>(null);
    const [showConnectField, setShowConnectField] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [targets, setTargets] = useState<SourceSelectionItem[]>([]);
    const [selection, setSelection] = useState<SourceSelectionItem[]>(existing.selection ?? []);
    const [progress, setProgress] = useState<[number, number]>([0, 0]);
    const [error, setError] = useState<{ step: Phase; message: string } | null>(null);
    const [repositoryDraftId, setRepositoryDraftId] = useState<number | null>(props.draftId ?? null);
    const [repositoryJobs, setRepositoryJobs] = useState<RepositoryImportJob[]>([]);
    const [activeRepoIds, setActiveRepoIds] = useState<number[]>(existing.staged?.repoIds ?? []);
    const [repositoryPollRevision, setRepositoryPollRevision] = useState(0);

    const ctrlRef = useRef<AbortController | null>(null);
    const importPromiseRef = useRef<Promise<{ staged: SourceConnection["staged"] }> | null>(null);
    const abortedRef = useRef(false);

    useEffect(() => {
        props.onRepositoryImportsChanged?.(repositoryJobs);
    }, [props.onRepositoryImportsChanged, repositoryJobs]);

    useEffect(() => {
        if (phase !== "importing" && phase !== "done") {
            props.onIntegrationImportChanged?.(null);
            return;
        }
        props.onIntegrationImportChanged?.({
            source,
            sourceName: cfg.name,
            itemType: cfg.itemsNoun,
            status: phase === "done" ? "completed" : "importing",
            current: progress[0],
            total: progress[1],
            selections: selection.map(item => item.name)
        });
    }, [cfg.itemsNoun, cfg.name, phase, progress, props.onIntegrationImportChanged, selection, source]);

    // Durable imports continue after the modal closes. Reopening the modal (or
    // reopening the draft after a page refresh) restores authoritative status
    // from the server and resumes one bounded polling loop.
    useEffect(() => {
        if (!usesDurableRepositoryImport || repositoryDraftId == null || activeRepoIds.length === 0) return;
        let disposed = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const selected = new Set(activeRepoIds);
        const refresh = async () => {
            try {
                const response = await getRepositoryImports(token, repositoryDraftId);
                if (disposed) return;
                const jobs = response.imports.filter(job => selected.has(job.repositoryId));
                setRepositoryJobs(jobs);
                if (jobs.length > 0 && jobs.every(job => !repositoryImportIsActive(job.status))) {
                    const completed = jobs.filter(job => job.status === "completed").length;
                    setProgress([completed, jobs.length]);
                    setPhase("done");
                } else if (jobs.some(job => repositoryImportIsActive(job.status))) {
                    setPhase("importing");
                    timer = setTimeout(refresh, 2_000);
                }
            } catch (reason) {
                if (!disposed) {
                    setError({ step: "ready", message: reason instanceof Error ? reason.message : "Unable to load repository progress." });
                }
            }
        };
        void refresh();
        return () => {
            disposed = true;
            if (timer) clearTimeout(timer);
        };
    }, [activeRepoIds, usesDurableRepositoryImport, repositoryDraftId, repositoryPollRevision, token]);

    // Look up the existing account on every mount. Refresh mode can hydrate an
    // already-linked product; the lookup still needs to prime provider-specific
    // driver state (Jira cloud id / ADO organization) before targets are listed.
    useEffect(() => {
        let cancelled = false;
        driver.checkExisting(token).then(acc => {
            if (!cancelled) setAccount(acc);
        }).catch(() => {
            if (!cancelled) setAccount(null);
        });
        return () => { cancelled = true; };
    }, [driver, token]);

    const loadTargets = useCallback(async () => {
        setError(null);
        setPhase("project");
        try {
            const list = await driver.listTargets(token);
            setTargets(list);
        } catch (e) {
            setError({ step: "project", message: e instanceof Error ? e.message : String(e) });
            setPhase("project");
        }
    }, [driver, token]);

    const runAuthorize = useCallback(async (configValue: string) => {
        setError(null);
        setPhase("verify");
        try {
            await driver.authorize(token, userId, configValue);
            const verified = await driver.checkExisting(token);
            setAccount(verified);
        } catch (e) {
            setError({ step: "account", message: e instanceof Error ? e.message : String(e) });
            setPhase("account");
            return;
        }
        await loadTargets();
    }, [driver, token, userId, loadTargets]);

    const handleUseAccount = () => {
        runAuthorize(fieldValue);
    };

    const handleSwitchAccount = () => {
        setError(null);
        setShowConnectField(false);
        setPhase("account");
    };

    const handleChangeTarget = async () => {
        setError(null);
        try {
            const existingAccount = await driver.checkExisting(token);
            setAccount(existingAccount);
            await loadTargets();
        } catch (e) {
            setError({ step: "project", message: e instanceof Error ? e.message : String(e) });
            setPhase("project");
        }
    };

    const handleConnectAnother = () => {
        if (cfg.connectField) {
            setShowConnectField(true);
        } else {
            runAuthorize("");
        }
    };

    const handleContinue = async () => {
        setError(null);
        setPhase("ready");
    };

    const handleConnect = async () => {
        setError(null);
        const ctrl = new AbortController();
        ctrlRef.current = ctrl;
        abortedRef.current = false;
        setPhase("importing");
        setProgress([0, selection.length]);
        const importPromise = driver.startImport(
            selection, token,
            (current, total) => setProgress([current, total]),
            ctrl.signal,
            { durableRepositoryImport: usesDurableRepositoryImport }
        );
        importPromiseRef.current = importPromise;
        try {
            const result = await importPromise;
            if (abortedRef.current) return;
            const connection: SourceConnection = {
                connected: true,
                account: account?.account,
                selection,
                staged: result.staged
            };
            if (usesDurableRepositoryImport) {
                if (!props.onRepositorySelectionStaged || !result.staged.repoIds?.length) {
                    throw new Error("The draft could not save the selected repositories.");
                }
                const persisted = await props.onRepositorySelectionStaged(connection);
                if (abortedRef.current) return;
                const nextRepositoryIds = new Set(result.staged.repoIds);
                await Promise.allSettled(
                    repositoryJobs
                        .filter(job => repositoryImportIsActive(job.status) && !nextRepositoryIds.has(job.repositoryId))
                        .map(job => cancelRepositoryImport(token, persisted.draftId, job.jobId))
                );
                const response = await scheduleRepositoryImports(
                    token,
                    persisted.draftId,
                    persisted.draftVersion,
                    result.staged.repoIds.map((repositoryId, index) => ({
                        repositoryId,
                        branch: selection[index]?.key ?? null
                    }))
                );
                if (abortedRef.current) return;
                const selected = new Set(result.staged.repoIds);
                setRepositoryDraftId(persisted.draftId);
                setActiveRepoIds(result.staged.repoIds);
                setRepositoryJobs(response.imports.filter(job => selected.has(job.repositoryId)));
                setPhase("importing");
                return;
            }
            setPhase("done");
            onComplete(connection);
            setTimeout(onClose, 700);
        } catch (e) {
            if (abortedRef.current) return;
            setError({ step: "ready", message: e instanceof Error ? e.message : String(e) });
            setPhase("ready");
        }
    };

    const handleClose = () => {
        if (phase === "importing") {
            if (usesDurableRepositoryImport) {
                // Closing is navigation, not cancellation. The durable jobs
                // remain visible when the draft/modal is reopened.
                onClose();
                return;
            }
            const ok = window.confirm("Cancel the import? Staged data from this run will be removed.");
            if (!ok) return;
            abortedRef.current = true;
            ctrlRef.current?.abort();
            importPromiseRef.current
                ?.then(result => onAborted({
                    processJobId: result.staged?.processJobId,
                    repoIds: result.staged?.repoIds
                }))
                .catch(() => onAborted({}));
            onClose();
            return;
        }
        onClose();
    };

    const handleRetryRepository = async (job: RepositoryImportJob) => {
        if (repositoryDraftId == null) return;
        setError(null);
        try {
            const updated = await retryRepositoryImport(token, repositoryDraftId, job.jobId);
            setRepositoryJobs(current => current.map(item => item.jobId === updated.jobId ? updated : item));
            setRepositoryPollRevision(value => value + 1);
            setPhase("importing");
        } catch (reason) {
            setError({ step: "ready", message: reason instanceof Error ? reason.message : "Repository retry failed." });
        }
    };

    const handleCancelRepository = async (job: RepositoryImportJob) => {
        if (repositoryDraftId == null) return;
        setError(null);
        try {
            const updated = await cancelRepositoryImport(token, repositoryDraftId, job.jobId);
            setRepositoryJobs(current => current.map(item => item.jobId === updated.jobId ? updated : item));
        } catch (reason) {
            setError({ step: "ready", message: reason instanceof Error ? reason.message : "Repository cancellation failed." });
        }
    };

    const handleDisconnect = async () => {
        if (usesDurableRepositoryImport && repositoryDraftId != null) {
            await Promise.allSettled(
                repositoryJobs
                    .filter(job => repositoryImportIsActive(job.status))
                    .map(job => cancelRepositoryImport(token, repositoryDraftId, job.jobId))
            );
        }
        onDisconnect();
    };

    const repositoryProgress = repositoryJobs.length > 0 ? (
        <div style={{ display: "grid", gap: 7, marginTop: 10 }} data-testid="repository-import-progress">
            {repositoryJobs.map(job => (
                <div key={job.jobId} style={{ border: "1px solid var(--divider)", borderRadius: 9, padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, fontWeight: 600 }}>
                            {job.repositoryName}
                        </span>
                        <span style={{ fontSize: 11, color: job.status === "failed" ? "var(--error, #dc2626)" : "var(--fg3)", textTransform: "capitalize" }}>
                            {job.status}
                        </span>
                        {job.status === "failed" && job.retryable && (
                            <button type="button" onClick={() => void handleRetryRepository(job)}>Retry</button>
                        )}
                        {repositoryImportIsActive(job.status) && (
                            <button type="button" onClick={() => void handleCancelRepository(job)}>Cancel</button>
                        )}
                    </div>
                    <div style={{ height: 5, marginTop: 6, borderRadius: 4, background: "var(--bg-input)", overflow: "hidden" }}>
                        <div style={{ width: `${job.progress}%`, height: "100%", background: job.status === "failed" ? "#dc2626" : "#103699" }} />
                    </div>
                    {(job.message || job.error) && (
                        <div style={{ marginTop: 4, fontSize: 11, color: job.status === "failed" ? "var(--error, #dc2626)" : "var(--fg4)" }}>
                            {job.error?.message ?? job.message}
                        </div>
                    )}
                </div>
            ))}
        </div>
    ) : null;

    const authorized = idx(phase) >= idx("project");
    const stepNo = phase === "account" ? 1 : phase === "verify" ? 2 : phase === "project" ? 3 : 4;
    const stepLabel: Record<Phase, string> = {
        account: "Choose account",
        verify: "Verifying connection",
        project: cfg.selectTitle,
        ready: `Sync ${cfg.itemsNoun}`,
        importing: `Importing ${cfg.itemsNoun}`,
        done: "Connected"
    };

    const authState = (): DotState => (phase === "account" ? "active" : "done");
    const verifyState = (): DotState =>
        idx(phase) >= idx("project") ? "done" : phase === "verify" ? "active" : "upcoming";
    const selectState = (): DotState =>
        idx(phase) >= idx("ready") ? "done" : phase === "project" ? "active" : "upcoming";
    const syncState = (): DotState =>
        idx(phase) >= idx("importing") ? (phase === "done" ? "done" : "active") : phase === "ready" ? "active" : "upcoming";

    const [current, total] = progress;
    const importedCount = usesDurableRepositoryImport && repositoryJobs.length > 0
        ? repositoryJobs.filter(job => job.status === "completed").length
        : existing.staged?.importedCount ?? existing.syncedCount ?? 0;

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 60, background: "rgba(15,23,42,0.42)",
                display: "flex", alignItems: "center", justifyContent: "center"
            }}
            onClick={handleClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: 470, maxHeight: "86%", overflow: "hidden auto",
                    background: "var(--bg-paper)", borderRadius: 16,
                    boxShadow: "0 24px 70px rgba(15,23,42,0.35)", padding: "20px 24px 22px"
                }}
            >
                {/* header */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <span style={{
                        width: 38, height: 38, borderRadius: 11, border: "1px solid var(--divider)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                    }}>
                        <NPGlyph name={cfg.glyph} size={20} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 650 }}>Connect {cfg.name}</div>
                        <div style={{ fontSize: 12, color: "var(--fg3)" }}>Step {stepNo} of 4 · {stepLabel[phase]}</div>
                    </div>
                    {authorized && phase !== "done" && (
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 11.5, fontWeight: 700, color: "#059669"
                        }}>
                            <span style={{ width: 7, height: 7, borderRadius: 4, background: "#10b981" }} />
                            Authorized
                        </span>
                    )}
                    <button
                        onClick={handleClose}
                        title="Close"
                        style={{
                            width: 28, height: 28, borderRadius: 9, border: "1px solid var(--divider)",
                            background: "transparent", cursor: "pointer", display: "inline-flex",
                            alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                        }}
                    >
                        <NPIcon name="x" size={12} sw={2.2} color="var(--fg3)" />
                    </button>
                </div>

                {phase === "done" && existing.connected && (
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", borderRadius: 11, background: "var(--bg-input)", marginBottom: 14 }}>
                            <NPGlyph name={cfg.glyph} size={16} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{existing.account}</div>
                                <div style={{ fontSize: 11.5, color: "var(--fg4)" }}>
                                    {importedCount} {cfg.itemsNoun} synced
                                </div>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 9 }}>
                            <button
                                type="button"
                                onClick={() => void handleDisconnect()}
                                style={{
                                    height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                    cursor: "pointer", fontFamily: "inherit", background: "transparent",
                                    border: "1px solid var(--divider)", color: "var(--fg2)"
                                }}
                            >
                                Disconnect
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleChangeTarget()}
                                style={{
                                    height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                    cursor: "pointer", fontFamily: "inherit", background: "transparent",
                                    border: "1px solid var(--divider)", color: "var(--np-brand)"
                                }}
                            >
                                Change selection
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                    cursor: "pointer", fontFamily: "inherit", background: "var(--np-brand)",
                                    border: "none", color: "#fff", boxShadow: "0 2px 8px rgba(16, 54, 153,0.3)"
                                }}
                            >
                                Close
                            </button>
                        </div>
                        {usesDurableRepositoryImport && repositoryProgress}
                        {error && <ErrorText message={error.message} />}
                    </div>
                )}

                {!(phase === "done" && existing.connected) && (
                    <>
                        {/* step 1: authorize */}
                        <NPCRow n={1} state={authState()} title="Authorize"
                            meta={authState() === "done" ? account?.account : null}
                            action={authState() === "done" && phase !== "importing" && phase !== "done"
                                ? <NPCAction label="Switch account" onClick={handleSwitchAccount} />
                                : null}>
                            {phase === "account" && (
                                <div style={{ marginTop: 8 }}>
                                    {account && (
                                        <>
                                            <div style={{ fontSize: 12.5, color: "var(--fg3)", marginBottom: 9 }}>
                                                1 {cfg.name} connection on file. Use it or add another.
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", borderRadius: 11, background: "var(--bg-input)", marginBottom: 10 }}>
                                                <NPGlyph name={cfg.glyph} size={16} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{account.account}</div>
                                                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg4)" }}>{account.host}</div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {(showConnectField || !account) && cfg.connectField && (
                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--fg3)", display: "block", marginBottom: 5 }}>
                                                {cfg.connectField.label}
                                            </label>
                                            <input
                                                value={fieldValue}
                                                onChange={e => setFieldValue(e.target.value)}
                                                placeholder={cfg.connectField.placeholder}
                                                style={{
                                                    width: "100%", padding: "8px 11px", borderRadius: 9,
                                                    border: "1px solid var(--divider)", fontSize: 12.5, fontFamily: "inherit"
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div style={{ display: "flex", gap: 9 }}>
                                        {(() => {
                                            const needsField = !account || showConnectField;
                                            const disabled = needsField && !!cfg.connectField?.required && !fieldValue.trim();
                                            return (
                                                <button type="button"
                                                    disabled={disabled}
                                                    onClick={handleUseAccount}
                                                    style={{
                                                        height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                                        cursor: disabled ? "default" : "pointer",
                                                        opacity: disabled ? 0.55 : 1,
                                                        fontFamily: "inherit", background: "var(--np-brand)",
                                                        border: "none", color: "#fff", boxShadow: "0 2px 8px rgba(16, 54, 153,0.3)"
                                                    }}>
                                                    Use this account
                                                </button>
                                            );
                                        })()}
                                        {account && !showConnectField && (
                                            <button type="button" onClick={handleConnectAnother}
                                                style={{
                                                    height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                                    cursor: "pointer", fontFamily: "inherit", background: "transparent",
                                                    border: "1px solid var(--divider)", color: "var(--fg2)"
                                                }}>
                                                Connect another account
                                            </button>
                                        )}
                                    </div>
                                    {error && error.step === "account" && <ErrorText message={error.message} />}
                                </div>
                            )}
                        </NPCRow>

                        {/* step 2: verify */}
                        <NPCRow n={2} state={verifyState()} title="Verify status"
                            meta={verifyState() === "done" ? "Connection confirmed" : null}>
                            {phase === "verify" && (
                                <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 7 }}>
                                    <NPSpinner size={18} />
                                    <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>Checking permissions…</span>
                                </div>
                            )}
                        </NPCRow>

                        {/* step 3: select project/repos */}
                        <NPCRow n={3} state={selectState()} title={cfg.selectTitle}
                            meta={selectState() === "done" && selection.length > 0
                                ? selection.map(s => s.name).join(", ")
                                : null}
                            action={selectState() === "done" && phase !== "importing" && phase !== "done"
                                ? <NPCAction label="Change" onClick={handleChangeTarget} />
                                : null}>
                            {phase === "project" && (
                                <div style={{ marginTop: 8 }}>
                                    {error && error.step === "project" ? (
                                        <div>
                                            <ErrorText message={error.message} />
                                            <button type="button" onClick={loadTargets}
                                                style={{
                                                    marginTop: 8, height: 30, padding: "0 12px", borderRadius: 9,
                                                    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                                    background: "transparent", border: "1px solid var(--divider)", color: "var(--fg2)"
                                                }}>
                                                Retry
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <TargetPicker kind={cfg.kind} targets={targets} selection={selection} onChange={setSelection} />
                                            <button type="button"
                                                disabled={selection.length === 0}
                                                onClick={handleContinue}
                                                style={{
                                                    marginTop: 10, height: 30, padding: "0 12px", borderRadius: 9,
                                                    fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                                                    cursor: selection.length === 0 ? "default" : "pointer",
                                                    opacity: selection.length === 0 ? 0.55 : 1,
                                                    background: "var(--np-brand)", border: "none", color: "#fff",
                                                    boxShadow: "0 2px 8px rgba(16, 54, 153,0.3)"
                                                }}>
                                                Continue
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </NPCRow>

                        {/* step 4: sync/import */}
                        <NPCRow n={4} state={syncState()} isLast
                            title={phase === "importing" ? `Importing ${cfg.itemsNoun}…` : `Sync ${cfg.itemsNoun}`}
                            meta={phase === "done" ? `${total} ${cfg.itemsNoun} synced` : null}>
                            {phase === "ready" && (
                                <div style={{ marginTop: 7 }}>
                                    <div style={{ fontSize: 12.5, color: "var(--fg3)", marginBottom: 10 }}>
                                        {cfg.syncNote(null, selection.length)}
                                    </div>
                                    <button type="button" onClick={handleConnect}
                                        style={{
                                            height: 30, padding: "0 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
                                            cursor: "pointer", fontFamily: "inherit", background: "var(--np-brand)",
                                            border: "none", color: "#fff", boxShadow: "0 2px 8px rgba(16, 54, 153,0.3)"
                                        }}>
                                        Connect
                                    </button>
                                    {error && error.step === "ready" && <ErrorText message={error.message} />}
                                </div>
                            )}
                            {phase === "importing" && (
                                <div style={{ marginTop: 9 }}>
                                    {usesDurableRepositoryImport && repositoryProgress}
                                    {!usesDurableRepositoryImport && <>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <div style={{ flex: 1, height: 7, borderRadius: 7, background: "var(--bg-input)", overflow: "hidden" }}>
                                            <div style={{
                                                width: `${total ? (current / total) * 100 : 0}%`, height: "100%",
                                                borderRadius: 7, background: "#103699", transition: "width .06s linear"
                                            }} />
                                        </div>
                                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "#103699", flex: "0 0 auto" }}>
                                            {current} / {total}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 6 }}>
                                        Adding {cfg.itemsNoun} to the Context Mesh
                                    </div>
                                    </>}
                                    {usesDurableRepositoryImport && (
                                        <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 8 }}>
                                            You can close this window. Importing will continue in the background.
                                        </div>
                                    )}
                                </div>
                            )}
                        </NPCRow>
                    </>
                )}
            </div>
        </div>
    );
}
